import { useRef, ReactNode, useCallback, useState } from 'react';

import { EditorCanvasContext, LayerLabelResolver } from './editor-canvas-context';
import { useCanvas } from './hooks/use-canvas';
import { useCanvasFit } from '../viewport/hooks/use-canvas-fit';
import { useCanvasZoom } from '../viewport/hooks/use-canvas-zoom';
import { useCanvasPan } from '../viewport/hooks/use-canvas-pan';
import { useCanvasObjects } from '../selection/hooks/use-canvas-objects';
import { useSnappingGuides } from '../snapping/hooks/use-snapping-guides';
import { useHistory } from '../history/hooks/use-history';
import { CanvasJSON } from './types';
import { useLayerControls } from '../layers/hooks/use-layer-controls';
import { ActiveTool } from './editor-canvas-context';
import { useSobreimpresoHydration } from '../text';
import { usePillHydration, usePillGroupWiring } from '../pill';
import { useCanvasClip } from '../viewport/hooks/use-canvas-clip';
import { preloadFonts } from '../text/utils/preload-fonts';
import { withCrossOrigin } from '../export/utils/canvas-json';
import { useResolutions } from '../resolution/hooks/use-resolutions';
import { hydrateCanvasVideos } from '../media/utils/video-hydration';
import { useMedia } from '../media';
import { usePieceStore } from '../model/piece-context';
import { applyGuard } from '../model/sync/apply-guard';
import type { PieceModel } from '../model/types';
import { prepareCanvasJson } from './utils/prepare-canvas-json';
import { findDuplicatePieceIds } from './utils/piece-id';

type EditorCanvasProviderProps = {
  children: ReactNode;
};

export const EditorCanvasProvider: React.FC<EditorCanvasProviderProps> = ({ children }) => {
  const { activeResolution } = useResolutions();
  const width = activeResolution.canvasWidth;
  const height = activeResolution.canvasHeight;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [activeTool, setActiveTool] = useState<ActiveTool>('select');

  const canvas = useCanvas(canvasRef);

  const { registerVideo, trackMediaLoad } = useMedia();
  const pieceStore = usePieceStore();

  const { baseZoom, fitViewport } = useCanvasFit(canvas, canvasContainerRef, width, height);
  const { zoom, zoomIn, zoomOut, resetZoom } = useCanvasZoom(canvas, baseZoom, width, height);
  useCanvasPan(canvas, activeTool);

  const fitToContainer = useCallback(() => {
    fitViewport();
    resetZoom();
  }, [fitViewport, resetZoom]);

  const {
    selectedObject,
    addRectangle,
    addCircle,
    addText,
    addPill,
    addSvg,
    addSobreimpreso,
    addAutoFitText,
    deleteSelected,
    clearCanvas
  } = useCanvasObjects(canvas, {
    designWidth: width,
    designHeight: height
  });
  const { clearHistory, undo, redo, canUndo, canRedo, transaction } = useHistory(canvas);

  useSnappingGuides(canvas, width, height);
  const hydratePills = usePillHydration(canvas);
  usePillGroupWiring(canvas); // re-attach pill/group listeners after undo/redo
  const hydrateSobreimpresos = useSobreimpresoHydration(canvas);

  const [clipToCanvas, setClipToCanvas] = useState(true);

  const [layerLabelResolver, setLayerLabelResolverState] = useState<LayerLabelResolver | null>(null);

  const setLayerLabelResolver = useCallback(
    (resolver: LayerLabelResolver | null) => setLayerLabelResolverState(() => resolver),
    []
  );

  const {
    layers,
    bringForward,
    sendBackward,
    moveNextTo,
    canBringForward,
    canSendBackward,
    focusLayer,
    toggleLayerInSelection
  } = useLayerControls(canvas, layerLabelResolver);

  const [layoutVersion, setLayoutVersion] = useState(0);

  useCanvasClip(canvas, clipToCanvas, width, height, layoutVersion);

  // Loads a fully-resolved canvas JSON into Fabric. The caller decides what JSON to pass.
  // Runs as one history transaction so the whole load lands as a single baseline entry.
  const loadProject = useCallback(
    async (canvasJSON: CanvasJSON) => {
      if (!canvas) return;

      clearHistory();
      await transaction(async () => {
        canvas.clear();

        const prev = canvas.renderOnAddRemove;
        canvas.renderOnAddRemove = false;

        // Prepare the JSON before Fabric hydrates it
        const safeJSON = await prepareCanvasJson(canvasJSON);
        await preloadFonts(safeJSON);
        await canvas.loadFromJSON(safeJSON);
        hydratePills();
        hydrateSobreimpresos();

        const duplicates = findDuplicatePieceIds(canvas);
        if (duplicates.length) console.warn(`Duplicate pieceId(s), the extras stay hidden: ${duplicates.join(', ')}`);

        await trackMediaLoad(hydrateCanvasVideos(canvas, registerVideo));

        canvas.renderOnAddRemove = prev;
        canvas.requestRenderAll();
        fitToContainer();

        // Audio rides in the JSON but isn't a canvas object; load it into the model.
        pieceStore.dispatch({ type: 'setAudioLayers', layers: canvasJSON.audioLayers ?? [] });
      });

      setLayoutVersion(v => v + 1);
    },
    [
      canvas,
      clearHistory,
      transaction,
      hydratePills,
      hydrateSobreimpresos,
      fitToContainer,
      registerVideo,
      trackMediaLoad,
      pieceStore
    ]
  );

  // Opens a saved piece from the model; the renderer rebuilds the canvas, the path undo uses.
  const loadPiece = useCallback(
    async (model: PieceModel) => {
      if (!canvas) return;
      clearHistory();
      await transaction(async () => {
        applyGuard.begin();
        canvas.clear();
        canvas.backgroundColor = 'transparent';
        applyGuard.end();
        pieceStore.dispatch({ type: 'replaceModel', model });
      });
      setLayoutVersion(v => v + 1);
    },
    [canvas, clearHistory, transaction, pieceStore]
  );

  return (
    <EditorCanvasContext.Provider
      value={{
        canvas,
        canvasRef,
        canvasContainerRef,

        selectedObject,

        activeTool,
        setActiveTool,

        addRectangle,
        addCircle,
        addText,
        addPill,
        addSvg,
        addSobreimpreso,
        addAutoFitText,
        deleteSelected,
        clearCanvas,

        zoom,
        zoomIn,
        zoomOut,
        fitToContainer,

        clipToCanvas,
        setClipToCanvas,

        undo,
        redo,
        canUndo,
        canRedo,
        transaction,
        loadProject,
        loadPiece,
        layoutVersion,

        layers,
        bringForward,
        sendBackward,
        moveNextTo,
        canBringForward,
        canSendBackward,
        focusLayer,
        toggleLayerInSelection,

        setLayerLabelResolver
      }}
    >
      {children}
    </EditorCanvasContext.Provider>
  );
};
